import {
  Input as ChakraInput
} from '@chakra-ui/react'
import type {
  InputProps
} from '@chakra-ui/react'
import React, {
  forwardRef
} from 'react'

import { BaseControl, type BaseControlProps } from '../base-control/base-control'
import { SuccessIcon } from './components/success-icon'
import { UserIssueIcon } from './components/user-issue-icon'
import { WarningIcon } from './components/warning-icon'

export type TextInputControlProps = BaseControlProps<InputProps>

/**
 * A Form Control component to handle user text input inside a form.
 * It is designed from the Material UI design and UX with tweaks from 3p UX team.
 *
 * The React ref is passed through to the input element for usage in scenarios like Chakra Popup.
 *
 * @see the story 'Components/TextInputControl' for states and features.
 */
export const TextInputControl = forwardRef<HTMLInputElement, TextInputControlProps>(
  ({
    controlProps,
    ...restProps
  }, ref) => {
    return (
      <BaseControl<InputProps>
        {...restProps}
        controlProps={controlProps}
        assignControlProps={(controlProps, props) => {
          return {
            ...props.RightAdornment ? { marginRight: '40px' } : null,
            ...controlProps
          }
        }}
        assignProps={(props, {
          isWarning,
          isUserWarning,
          isSuccess
        }) => {
          // Add icons according to the state of the control
          let RightAdornment: any = restProps.RightAdornment || null

          if (isWarning) {
            RightAdornment = <WarningIcon />
          }

          if (isUserWarning) {
            RightAdornment = <UserIssueIcon />
          }

          if (isSuccess) {
            RightAdornment = <SuccessIcon />
          }

          return {
            ...props,
            LeftAdornment: restProps.LeftAdornment || null,
            RightAdornment
          }
        }}
        renderControl={(props) => (
          <ChakraInput
            variant={'unstyled'}
            autoComplete={'off'}
            width={'100%'}
            px={'16px'}
            {...props}
            ref={ref}
          />
        )}
      />
    )
  })
